import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { OnboardingProgress } from '@tailoring-crm/shared';

/**
 * How far along the shop is, as one bar and one sentence.
 *
 * The bar measures what has been SET UP; skipped steps are counted separately and named in the
 * sentence beside it. Rolling the two together would either tell a shop with no GST
 * registration that it is 83% finished forever, or congratulate it for skipping — and neither
 * is what the owner wants to know, which is "is there anything left for me to do?".
 */
export function SetupProgress({ progress }: { progress: OnboardingProgress }): ReactElement {
  const { t } = useTranslation();
  const { summary } = progress;

  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={summary.percentComplete}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('onboarding.progressLabel')}
      >
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${String(summary.percentComplete)}%` }}
        />
      </div>
      <p className="text-sm text-muted-foreground" role="status">
        {t('onboarding.progressSummary', {
          completed: summary.completed,
          total: summary.total,
        })}
        {summary.skipped > 0
          ? ` ${t('onboarding.progressSkipped', { count: summary.skipped })}`
          : ''}
      </p>
    </div>
  );
}
